import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ChatMessage, 
  ConnectionStatus, 
  ClientMessage, 
  ServerMessage, 
  ChatResponseMessage, 
  ErrorMessage,
  UserMessage,
  MoreMessagesMessage,
  MessageSender,
  CommandReceivedMessage,
  CommandStatusMessage,
  ClarificationRequestMessage,
  CommandAcceptedMessage,
  CommandErrorMessage,
  InspectionResultsMessage
} from '../types/chat';
import TokenManager from '../utils/tokenManager';

const tokenManager = TokenManager.getInstance();

export const useWebSocketChat = (environmentId: string) => {
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isSessionRestored, setIsSessionRestored] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pendingCommands, setPendingCommands] = useState<Map<string, any>>(new Map());
  const [clarificationRequests, setClarificationRequests] = useState<Map<string, any>>(new Map());
  const [activeCorrelationId, setActiveCorrelationId] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(async () => {
    if (!environmentId) return;

    try {
      setConnectionStatus('connecting');
      // Clear messages when starting new connection
      setMessages([]);
      setCurrentSessionId(null);
      setIsSessionRestored(false);
      
      const token = await tokenManager.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const wsEndpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT;
      if (!wsEndpoint) {
        throw new Error('WebSocket endpoint not configured');
      }

      const wsUrl = `${wsEndpoint}?environmentId=${environmentId}&token=${encodeURIComponent(token)}`;
      const websocket = new WebSocket(wsUrl);
      
      websocket.onopen = () => {
        console.log('WebSocket connected successfully');
        setConnectionStatus('connected');
        setWs(websocket);
        setIsSessionRestored(false); // Reset session restore state
        
        // Send sessionRestore command to get chat history
        websocket.send(JSON.stringify({ type: 'session_restore', token }));
        
        // Send ping every 30 seconds to keep connection alive
        pingIntervalRef.current = setInterval(async () => {
          if (websocket.readyState === WebSocket.OPEN) {
            const pingToken = await tokenManager.getIdToken();
            websocket.send(JSON.stringify({ type: 'ping', token: pingToken }));
          }
        }, 30000);
      };
      
      websocket.onmessage = (event) => {
        try {
          const message: ServerMessage = JSON.parse(event.data);
          
          if (message.type === 'response') {
            const responseMessage = message as ChatResponseMessage;
            setMessages(prev => [...prev, {
              id: `${Date.now()}-${Math.random()}`,
              sender: 'agent',
              content: responseMessage.content,
              timestamp: new Date().toISOString(),
              sessionId: responseMessage.sessionId,
              status: 'sent'
            }]);
            
            if (responseMessage.sessionId) {
              setCurrentSessionId(responseMessage.sessionId);
            }
          } else if (message.type === 'user_message') {
            const userMessage = message as UserMessage;
            setMessages(prev => [...prev, {
              id: userMessage.messageId,
              sender: userMessage.sender,
              content: userMessage.content,
              timestamp: userMessage.timestamp,
              sessionId: userMessage.sessionId,
              status: 'sent'
            }]);
            
            if (userMessage.sessionId) {
              setCurrentSessionId(userMessage.sessionId);
            }
          } else if (message.type === 'session_resumed') {
            console.log('Session resumed:', message.sessionId, `${message.messages?.length || 0} messages`);
            setCurrentSessionId(message.sessionId);
            setIsSessionRestored(true);
            setHasMore(message.hasMore || false);
            
            if (message.messages && message.messages.length > 0) {
              // Replace current messages with restored history
              setMessages(message.messages.map((msg: any) => ({
                ...msg,
                id: msg.id || `${Date.now()}-${Math.random()}` // Ensure unique IDs
              })));
            } else {
              // Clear messages if no history
              setMessages([]);
            }
          } else if (message.type === 'more_messages') {
            const moreMessage = message as MoreMessagesMessage;
            setHasMore(moreMessage.hasMore);
            
            if (moreMessage.messages && moreMessage.messages.length > 0) {
              // Prepend older messages to the beginning
              setMessages(prev => [
                ...moreMessage.messages.map((msg: any) => ({
                  ...msg,
                  id: msg.id || `${Date.now()}-${Math.random()}`
                })),
                ...prev
              ]);
            }
          } else if (message.type === 'command_queued') {
            const queuedMessage = message as any;
            setMessages(prev => [...prev, {
              id: `${Date.now()}-${Math.random()}`,
              sender: 'system' as MessageSender,
              content: queuedMessage.message,
              timestamp: new Date().toISOString(),
              sessionId: queuedMessage.sessionId,
              status: 'sent'
            }]);
            
            if (queuedMessage.sessionId) {
              setCurrentSessionId(queuedMessage.sessionId);
            }
          } else if (message.type === 'command_received') {
            const commandMessage = message as CommandReceivedMessage;
            setMessages(prev => [...prev, {
              id: commandMessage.messageId,
              sender: 'agent' as MessageSender,
              content: commandMessage.content,
              timestamp: commandMessage.timestamp,
              status: 'sent'
            }]);
            
            setActiveCorrelationId(commandMessage.correlationId);
            setPendingCommands(prev => new Map(prev.set(commandMessage.correlationId, {
              state: 'received',
              content: commandMessage.content
            })));
          } else if (message.type === 'command_status') {
            const statusMessage = message as CommandStatusMessage;
            setMessages(prev => [...prev, {
              id: statusMessage.messageId,
              sender: statusMessage.sender,
              content: statusMessage.content,
              timestamp: statusMessage.timestamp,
              status: 'sent'
            }]);
            
            setActiveCorrelationId(statusMessage.correlationId);
            setPendingCommands(prev => new Map(prev.set(statusMessage.correlationId, {
              state: 'processing',
              content: statusMessage.content
            })));
          } else if (message.type === 'clarification_request') {
            const clarificationMessage = message as ClarificationRequestMessage;
            setMessages(prev => [...prev, {
              id: `clarification-${Date.now()}`,
              sender: 'agent' as MessageSender,
              content: clarificationMessage.question,
              timestamp: clarificationMessage.timestamp,
              status: 'sent'
            }]);
            
            setActiveCorrelationId(clarificationMessage.correlationId);
            setClarificationRequests(prev => new Map(prev.set(clarificationMessage.correlationId, {
              question: clarificationMessage.question,
              timestamp: clarificationMessage.timestamp
            })));
          } else if (message.type === 'command_accepted') {
            const acceptedMessage = message as CommandAcceptedMessage;
            setMessages(prev => [...prev, {
              id: `accepted-${Date.now()}`,
              sender: 'system' as MessageSender,
              content: acceptedMessage.message,
              timestamp: acceptedMessage.timestamp,
              status: 'sent'
            }]);
            
            setActiveCorrelationId(acceptedMessage.correlationId);
            setPendingCommands(prev => new Map(prev.set(acceptedMessage.correlationId, {
              state: 'accepted',
              content: acceptedMessage.message
            })));
          } else if (message.type === 'command_error') {
            const errorMessage = message as CommandErrorMessage;
            setMessages(prev => [...prev, {
              id: `error-${Date.now()}`,
              sender: 'system' as MessageSender,
              content: errorMessage.error,
              timestamp: errorMessage.timestamp,
              isError: true,
              status: 'error'
            }]);
            
            setActiveCorrelationId(null); // Clear correlation on error
            setPendingCommands(prev => new Map(prev.set(errorMessage.correlationId, {
              state: 'error',
              content: errorMessage.error
            })));
          } else if (message.type === 'inspection_results') {
            const inspectionMessage = message as InspectionResultsMessage;
            setMessages(prev => [...prev, {
              id: `inspection-${Date.now()}`,
              sender: 'agent' as MessageSender,
              content: 'Environment inspection completed',
              timestamp: inspectionMessage.timestamp,
              status: 'sent'
            }]);
            
            setActiveCorrelationId(null); // Clear correlation on completion
            setPendingCommands(prev => new Map(prev.set(inspectionMessage.correlationId, {
              state: 'completed',
              content: 'Environment inspection completed',
              inspectionData: inspectionMessage.data
            })));
          } else if (message.type === 'error') {
            const errorMessage = message as ErrorMessage;
            console.error('Chat error:', errorMessage.error);
            setMessages(prev => [...prev, {
              id: `${Date.now()}-${Math.random()}`,
              sender: 'system',
              content: `Error: ${errorMessage.error}`,
              timestamp: new Date().toISOString(),
              isError: true,
              status: 'error'
            }]);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
      
      websocket.onclose = (event) => {
        console.log('WebSocket closed - Code:', event.code, 'Reason:', event.reason, 'WasClean:', event.wasClean);
        setConnectionStatus('disconnected');
        setWs(null);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Log reconnection decision
        if (event.code !== 1000) {
          console.log('Abnormal closure detected, will reconnect in 3 seconds');
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        } else {
          console.log('Normal closure, not reconnecting');
        }
      };
      
      websocket.onerror = (error) => {
        console.error('WebSocket error occurred:', error);
        setConnectionStatus('error');
      };
      
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [environmentId]);

  const sendMessage = useCallback(async (content: string, metadata?: any) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const token = await tokenManager.getIdToken();
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    let message: ClientMessage & { token: string };
    
    if (content.startsWith('/')) {
      const parts = content.slice(1).split(' ');
      const command = parts[0];
      const prompt = parts.slice(1).join(' ');
      
      // Clear active correlation when starting new command
      setActiveCorrelationId(null);
      
      message = {
        type: 'command',
        sessionId: currentSessionId || undefined,
        command,
        prompt,
        metadata,
        token
      };
    } else {
      message = {
        type: 'chat',
        sessionId: currentSessionId || undefined,
        content,
        metadata,
        correlationId: activeCorrelationId || undefined,
        token
      };
    }
    
    ws.send(JSON.stringify(message));
    
    // Add user message to local state
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      sender: 'user',
      content,
      timestamp: new Date().toISOString(),
      status: 'sent'
    }]);
  }, [ws, currentSessionId, activeCorrelationId]);

  const loadMoreMessages = useCallback(async () => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !hasMore || !currentSessionId || messages.length === 0) {
      return;
    }

    const token = await tokenManager.getIdToken();
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    const oldestMessageId = messages[0].id;
    const message: ClientMessage & { token: string } = {
      type: 'load_more_messages',
      sessionId: currentSessionId,
      lastMessageId: oldestMessageId,
      token
    };
    
    ws.send(JSON.stringify(message));
  }, [ws, hasMore, currentSessionId, messages]);

  const sendClarificationResponse = useCallback(async (correlationId: string, response: string) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const token = await tokenManager.getIdToken();
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    const message: any = {
      type: 'clarification_response',
      correlationId,
      content: response,
      token
    };
    
    ws.send(JSON.stringify(message));
    
    // Remove from clarification requests
    setClarificationRequests(prev => {
      const newMap = new Map(prev);
      newMap.delete(correlationId);
      return newMap;
    });
    
    // Add user response to local state
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      sender: 'user',
      content: response,
      timestamp: new Date().toISOString(),
      status: 'sent'
    }]);
  }, [ws]);

  const disconnect = useCallback(() => {
    console.log('Disconnect called - cleaning up WebSocket connection');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (ws) {
      console.log('Closing WebSocket connection');
      ws.close(1000, 'User disconnected');
    }
  }, [ws]);

  useEffect(() => {
    if (environmentId) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [environmentId, connect]);

  return {
    messages,
    connectionStatus,
    sendMessage,
    sendClarificationResponse,
    loadMoreMessages,
    connect,
    disconnect,
    currentSessionId,
    isSessionRestored,
    hasMore,
    pendingCommands,
    clarificationRequests,
    activeCorrelationId
  };
};